import { memo, useEffect, useState } from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle, Grid } from '@mui/material';

import CurrencySubContent from 'components/ui/text-formats/CurrencySubContent';
import FormSimpleSelectField from 'components/ui/form/FormSimpleSelectField';
import FormAutocompleteField from 'components/ui/form/FormAutocompleteField';
import { currencyTypeSelectOptions } from 'shared/selectOptionsList';
import FormCurrencyField from 'components/ui/form/FormCurrencyField';
import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormDateField from 'components/ui/form/FormDateField';
import FormTextField from 'components/ui/form/FormTextField';
import useNotification from 'hooks/useNotification';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useCurrency from 'hooks/useCurrency';
import useMenu from 'hooks/useMenu';
import moment from 'moment';

const variables = [
  {
    key: '${object}',
    name: 'Obyekt',
  },
  {
    key: '${block}',
    name: 'Blok',
  },
  {
    key: '${entrance}',
    name: 'Padez',
  },
  {
    key: '${stage}',
    name: 'Qavat',
  },
  {
    key: '${home}',
    name: 'Uy raqam',
  },
  {
    key: '${number}',
    name: 'Tartib raqam',
  },
];

const validationSchema = yup.object({
  sum: yup.number().required('payment.addModal.validation.sum'),
  type_id: yup.number().required('payment.addModal.validation.typeId'),
  // number: yup.number().required('payment.addModal.validation.paymentId'),
  date: yup
    .date()
    .typeError('payment.addModal.validation.dateValid')
    .default(new Date())
    .required('payment.addModal.validation.date'),
  isvalute: yup.string().optional(),
});

const PaymentAddModal = (props) => {
  const { open, setOpen, refetch, data: contractData } = props;
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const sendNotification = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currencyData } = useCurrency();
  const { hasChild } = useMenu();
  const canBackdate = hasChild('PAYMENTS', 'PAYMENT_BACKDATE');

  const formik = useFormik({
    initialValues: {
      sum: '',
      number: '',
      comment: '',
      type_id: '',
      date: new Date(),
      isvalute: contractData?.isvalute || '0',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const response = await axiosPrivate.post(
          `/admin/payment/store/${contractData?.id}`,
          JSON.stringify(values),
          { headers: { 'Content-Type': 'application/json' } },
        );
        if (response.data && response.data.status) {
          sendNotification({
            msg: t('payment.addModal.alerts.success', {
              name: contractData?.name,
            }),
            variant: 'success',
          });
          setIsSubmitting(false);
          handleClose();
          refetch();
        }
      } catch (error) {
        sendNotification({
          msg: error?.response?.data?.message || error?.message,
          variant: 'error',
        });
        setIsSubmitting(false);
      }
    },
  });

  const getContractNameFields = (key) => {
    switch (key) {
      case '${object}':
        return contractData?.homes?.blocks?.objects?.name || '';
      case '${block}':
        return contractData?.homes?.blocks?.name || '';
      case '${entrance}':
        return contractData?.homes?.blocks?.objects?.padez;
      case '${stage}':
        return contractData?.homes?.blocks?.objects?.stage || '';
      case '${home}':
        return contractData?.homes?.number || '';
      case '${number}':
        return contractData?.homes?.blocks?.objects?.p_number || '';
      default:
        return '';
    }
  };

  useEffect(() => {
    const p_name = contractData?.homes?.blocks?.objects?.p_name;
    if (p_name) {
      let contractName = p_name;
      variables?.forEach((v) => {
        if (contractName?.includes(v?.key)) {
          contractName = contractName?.replaceAll(
            v?.key,
            getContractNameFields(v?.key),
          );
        }
      });
      formik.setFieldValue('number', contractName);
    }
  }, [contractData?.homes?.blocks?.objects?.p_name]);

  const handleCurrencyChange = (value) => {
    formik.setFieldValue('isvalute', value, true);
    if (currencyData && currencyData.sum) {
      if (value == '1') {
        formik.setFieldValue(
          'sum',
          parseFloat(
            parseFloat(formik.values.sum / currencyData.sum).toFixed(1),
          ),
          true,
        );
      } else if (value == '0') {
        formik.setFieldValue(
          'sum',
          parseFloat(formik.values.sum * currencyData.sum),
          true,
        );
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      maxWidth='lg'
      disableEscapeKeyDown={true}
      className='!base-border !shadow-none'
      PaperProps={{
        sx: { boxShadow: 'none', borderRadius: '25px', maxWidth: 671 },
      }}
    >
      <DialogTitle
        id='alert-dialog-title'
        className='!bg-[#CDCDCDC7] !text-center !border-b !border-b-[#E6E6E6]'
      >
        <span className='pr-5 font-semibold text-[21px] text-[#374957]'>
          {t('payment.addModal.title')}
        </span>
        {/* <div className='close-btn-wrapper'>
          <IconButton variant='onlyIcon' color='primary' onClick={handleClose}>
            <i className='bi bi-x' />
          </IconButton>
        </div> */}
      </DialogTitle>

      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Grid
            container
            spacing={{ xs: 2, sm: 2 }}
            rowSpacing={1}
            columns={{ xs: 12, sm: 12 }}
            className='!my-3'
          >
            <Grid item={true} sm={6} xs={12}>
              <FormSimpleSelectField
                delay={0.1}
                label={t('common.fields.currency')}
                options={currencyTypeSelectOptions}
                fieldName='isvalute'
                formik={formik}
                changeFn={handleCurrencyChange}
                itemValue='code'
                itemLabel='label'
              />
            </Grid>
            <Grid item={true} sm={6} xs={12}>
              <FormCurrencyField
                delay={0.2}
                label={t('common.fields.paymentAmount')}
                fieldName='sum'
                formik={formik}
                decimalScale={1}
              />
              {formik.values.isvalute == '1' && !isNaN(formik.values.sum) && (
                <CurrencySubContent value={formik.values.sum} delay={0.2} />
              )}
            </Grid>
            <Grid item={true} sm={6} xs={12}>
              <FormAutocompleteField
                delay={0.3}
                label={t('common.fields.typeId')}
                fieldName='type_id'
                formik={formik}
                path={'/dictionary/types'}
              />
            </Grid>
            <Grid item={true} sm={6} xs={12}>
              <FormDateField
                delay={0.4}
                label={t('common.fields.paymentDate')}
                fieldName='date'
                formik={formik}
                minDate={canBackdate ? null : moment().startOf('day')}
              />
            </Grid>
            <Grid item={true} sm={12} xs={12}>
              <FormTextField
                delay={0.5}
                label={t('common.table.paymentId')}
                fieldName='number'
                formik={formik}
              />
            </Grid>
            <Grid item={true} sm={12} xs={12}>
              <FormTextField
                delay={0.6}
                label={t('common.fields.comment')}
                fieldName='comment'
                formik={formik}
                rows={4}
                multiline
              />
            </Grid>
            <Grid item={true} sm={12} xs={12}>
              <div className='flex justify-end'>
                <FormActionButtons
                  delay={0.7}
                  isSubmitting={isSubmitting}
                  formType='dialog'
                  setOpen={setOpen}
                  reset={formik.resetForm}
                />
              </div>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default memo(PaymentAddModal);
